import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PmsConnectionPayload } from '@models/property.model';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { HttpService } from '@services/http.service';
import { NotificationService } from '@services/notification.service';
import { EMPTY, Subject, switchMap, takeUntil } from 'rxjs';

@Component({
  selector: 'app-pms-auth-modal',
  standalone: false,
  templateUrl: './pms-auth-modal.component.html',
  styleUrls: ['./pms-auth-modal.component.scss'],
})
export class PmsAuthModalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() hotelId: string = '';
  @Input() selectedProvider: string = '';
  @Output() authenticated = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  public pmsForm: FormGroup;
  public providers: any[] = [];
  public showPassword: boolean = false;
  public isAuthenticating: boolean = false;

  constructor(
    public modalRef: BsModalRef,
    private formBuilder: FormBuilder,
    private httpService: HttpService,
    private notification: NotificationService,
  ) {}

  ngOnInit(): void {
    this.createPmsForm();
    this.fetchProviders();
  }

  private createPmsForm(): void {
    this.pmsForm = this.formBuilder.group({
      provider: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }

  public fetchProviders(): void {
    this.httpService.getData('hotel/pms/providers').pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res.data) {
          this.providers = res.data;
          if (this.selectedProvider) {
            const match = this.providers.find(p => p.provider_key === this.selectedProvider);
            if (match) {
              this.pmsForm.get('provider').setValue(this.selectedProvider);
            }
          }
        }
      },
      error: (err) => {
        console.error('Error fetching PMS providers:', err);
      },
    });
  }

  public onAuthenticate(): void {
    if (!this.pmsForm.valid) {
      this.pmsForm.markAllAsTouched();
      this.notification.showInfo('Please provide all required PMS information');
      return;
    }

    const { provider, email, password } = this.pmsForm.value;

    this.isAuthenticating = true;

    // Step 1: Search for pms_property_id using provider + email, then
    // Step 2: create/update the PMS connection with the resolved property id.
    this.httpService
      .getData(`hotel/pms/hotels/search?provider=${provider}&email=${email}`)
      .pipe(
        switchMap((res) => {
          if (res.status == 'success' && res.data?.hotels?.length > 0) {
            const pmsHotel = res.data.hotels[0];
            return this.httpService.postData(
              'hotel/pms/add-or-update',
              this.buildPmsConnectionPayload(provider, email, password, String(pmsHotel.id)),
            );
          }
          this.isAuthenticating = false;
          this.notification.showError('Could not find property on the PMS provider. Please check your credentials.');
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.isAuthenticating = false;
          this.notification.showSuccess('Connection successful');
          this.pmsForm.reset();
          this.modalRef.hide();
          this.authenticated.emit();
        },
        error: (err) => {
          this.isAuthenticating = false;
          this.notification.showError(
            err.error?.message || 'Failed to connect PMS. Please try again.',
          );
          console.error('Error connecting PMS:', err);
        },
      });
  }

  private buildPmsConnectionPayload(
    provider: string,
    email: string,
    password: string,
    pmsPropertyId: string,
  ): PmsConnectionPayload {
    return {
      hotel_id: this.hotelId,
      provider,
      channel_type: 'WAKANOW',
      pms_property_id: pmsPropertyId,
      is_connected: true,
      credentials: {
        username: email,
        password,
        hotelCode: pmsPropertyId,
      },
    };
  }

  public onCancel(): void {
    this.pmsForm.reset();
    this.modalRef.hide();
    this.cancelled.emit();
  }

  public togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
